/**
 * Mega navigation - see layouts/default_mega_header.twig and the mega
 * navigation part of _designs/design_modern.scss.
 *
 * Desktop (from `--mega-nav-break` up): panels open on hover for fine pointers
 * and on click for everyone, one at a time. They close on Escape (focus goes
 * back to the trigger), when keyboard focus leaves the navigation, on a click
 * outside and on scroll.
 *
 * Below the break the burger buttons (`.js-nav__button`, in the header and in
 * the floating mobile bar) open the navigation as a drawer; the triggers then
 * work as accordions. Escape closes the drawer.
 *
 * A trigger can be a link (`.mega-nav__trigger--link`): with a mouse a click
 * follows it (hover opens the panel), on touch screens the first tap opens the
 * panel and the second one follows the link. In the drawer the link always
 * follows and its toggle (`.js-mega-nav__toggle`) opens the accordion.
 * Keyboard users open its panel with arrow down.
 */
export function megaNav() {
	const nav = document.querySelector('.js-mega-nav')
	if (!nav) return

	const body = document.body
	const items = Array.from(nav.querySelectorAll('.js-mega-nav__item'))
	const toggles = Array.from(document.querySelectorAll('.js-nav__button'))
	const canHover = window.matchMedia('(hover: hover) and (pointer: fine)')
	const breakValue = getComputedStyle(document.documentElement).getPropertyValue('--mega-nav-break').trim() || '80rem'
	const desktop = window.matchMedia(`(min-width: ${breakValue})`)
	const HOVER_OPEN_DELAY = 80
	const HOVER_CLOSE_DELAY = 200

	let openTimer = null
	let closeTimer = null

	const isDesktop = () => desktop.matches
	const isOpen = () => nav.classList.contains('is-open')
	const drawerOpen = () => nav.classList.contains('is-mobile-open')
	const triggerOf = (item) => item.querySelector('.js-mega-nav__trigger')
	const toggleOf = (item) => item.querySelector('.js-mega-nav__toggle')

	const clearTimers = () => {
		clearTimeout(openTimer)
		clearTimeout(closeTimer)
	}

	// panels / accordions -------------------------------------------------------
	const closeItem = (item) => {
		item.classList.remove('is-active')
		const trigger = triggerOf(item)
		if (trigger) trigger.setAttribute('aria-expanded', 'false')
		const toggle = toggleOf(item)
		if (toggle) toggle.setAttribute('aria-expanded', 'false')
	}

	const closeAll = () => {
		clearTimers()
		items.forEach(closeItem)
		nav.classList.remove('is-open')
		body.classList.remove('js-mega-open')
	}

	const openItem = (item) => {
		clearTimers()
		items.forEach((other) => {
			if (other !== item) closeItem(other)
		})
		item.classList.add('is-active')
		const trigger = triggerOf(item)
		if (trigger) trigger.setAttribute('aria-expanded', 'true')
		const toggle = toggleOf(item)
		if (toggle) toggle.setAttribute('aria-expanded', 'true')
		nav.classList.add('is-open')
		// the blurred backdrop only exists for the desktop panels
		if (isDesktop()) body.classList.add('js-mega-open')
	}

	// drawer --------------------------------------------------------------------
	const setDrawer = (open) => {
		nav.classList.toggle('is-mobile-open', open)
		body.classList.toggle('js-nav-open', open)
		toggles.forEach((toggle) => {
			toggle.classList.toggle('is-active', open)
			const button = toggle.matches('button') ? toggle : toggle.querySelector('button')
			if (button) button.setAttribute('aria-expanded', String(open))
		})

		if (open) {
			const first = nav.querySelector('.mega-nav__trigger')
			if (first) first.focus()
		} else {
			closeAll()
		}
	}

	// events --------------------------------------------------------------------
	toggles.forEach((toggle) => {
		const button = toggle.matches('button') ? toggle : toggle.querySelector('button')
		if (button) button.setAttribute('aria-expanded', 'false')
		toggle.addEventListener('click', () => setDrawer(!drawerOpen()))
	})

	items.forEach((item) => {
		const trigger = triggerOf(item)
		if (!trigger) return

		const isLink = trigger.matches('a[href]')

		trigger.addEventListener('click', (event) => {
			if (isLink) {
				// follow the link once its panel is open, right away with a mouse and
				// always in the drawer (the toggle next to it opens the accordion there)
				if (item.classList.contains('is-active') || !isDesktop() || canHover.matches) return
				event.preventDefault()
				openItem(item)
				return
			}
			item.classList.contains('is-active') ? closeAll() : openItem(item)
		})

		const toggle = toggleOf(item)
		if (toggle) {
			toggle.addEventListener('click', () => {
				item.classList.contains('is-active') ? closeAll() : openItem(item)
			})
		}

		if (isLink) {
			trigger.addEventListener('keydown', (event) => {
				if (event.key !== 'ArrowDown') return
				event.preventDefault()
				openItem(item)
				const first = item.querySelector('.mega-nav__panel a')
				if (first) first.focus()
			})
		}

		item.addEventListener('mouseenter', () => {
			if (!isDesktop() || !canHover.matches) return
			clearTimers()
			openTimer = setTimeout(() => openItem(item), HOVER_OPEN_DELAY)
		})

		item.addEventListener('mouseleave', () => {
			if (!isDesktop() || !canHover.matches) return
			clearTimers()
			closeTimer = setTimeout(closeAll, HOVER_CLOSE_DELAY)
		})
	})

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return

		if (drawerOpen()) {
			setDrawer(false)
			const toggle = toggles.find((t) => t.offsetParent !== null)
			const button = toggle && (toggle.matches('button') ? toggle : toggle.querySelector('button'))
			if (button) button.focus()
			return
		}

		if (isOpen()) {
			const activeTrigger = nav.querySelector('.js-mega-nav__item.is-active .js-mega-nav__trigger')
			closeAll()
			if (activeTrigger) activeTrigger.focus()
		}
	})

	// keyboard focus leaving the navigation; relatedTarget is null for plain
	// clicks on non-focusable panel areas, those must not close the panel
	nav.addEventListener('focusout', (event) => {
		if (!isDesktop()) return
		if (event.relatedTarget && !nav.contains(event.relatedTarget)) closeAll()
	})

	document.addEventListener('click', (event) => {
		if (!isDesktop() || !isOpen()) return
		if (!nav.contains(event.target)) closeAll()
	})

	window.addEventListener(
		'scroll',
		() => {
			if (isDesktop() && isOpen()) closeAll()
		},
		{ passive: true }
	)

	// switching between the two modes resets everything
	desktop.addEventListener('change', () => {
		setDrawer(false)
		closeAll()
	})
}
